import { Injectable } from '@angular/core';
import { ConnectIdentityConfig } from './connect-identity-config';
import { VsnFeedbackConfig } from './vsn-feedback-config';
import { HOSTNAME } from './hostname';
import { ENV } from './env';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DomainService {

  getConnectIdentityConfig(hostname: string): ConnectIdentityConfig {
    const connectIdentity: { [key: string]: any } = environment.connectIdentity;
    const env = this.getEnviroment(hostname);
    const config: ConnectIdentityConfig = connectIdentity[env];
    // A second hostname shares its environment's config, but Connect has to send the user back to the
    // host they came from - otherwise logging in on the Scarlet domain lands on the Visma one.
    if (hostname === HOSTNAME.stagScarlet || hostname === HOSTNAME.prodScarlet) {
      return {
        ...config,
        redirect_uri: `https://${hostname}/callback`,
        post_logout_redirect_uri: `https://${hostname}`
      };
    }
    return config;
  }

  getBaseUrl(hostname: string): string {
    const env = this.getEnviroment(hostname);
    const api: { [key: string]: string } = environment.api;
    return api[env];
  }

  getBaseUrlForTenants(hostname: string, excludeApiSuffix = false): string {
    let env = this.getEnviroment(hostname);
    let api: { [key: string]: string };
    // By environment, not hostname: both prod domains must reach the same API.
    if (env === ENV.prod) {
      api = environment.api;
      return api[env];
    } else {
      api = environment.provisioningApi;
      return excludeApiSuffix ? api[env] : `${api[env]}/api`;
    }
  }

  getVismaHomeUri(hostname: string): string {
    const env = this.getEnviroment(hostname);
    const uri: { [key: string]: string } = environment.vismaHomeUri;
    return uri[env];
  }

  getGaiaChatApiUrl(hostname: string): string {
    const env = this.getEnviroment(hostname);
    const api: { [key: string]: string } = environment.gaiaChatApi;
    return api[env];
  }

  getVsnProfilePictureBaseUrl(hostname: string): string {
    const env = this.getEnviroment(hostname);
    const urls: { [key: string]: string } = environment.vsnNavigation.profilePictureBaseUrl;
    return urls[env];
  }

  getVsnMyProfileUrl(hostname: string): string {
    const env = this.getEnviroment(hostname);
    const urls: { [key: string]: string } = environment.vsnNavigation.myProfileUrl;
    return urls[env];
  }

  getVsnLandingPageUrl(hostname: string): string {
    const env = this.getEnviroment(hostname);
    const urls: { [key: string]: string } = environment.vsnNavigation.landingPageUrl;
    return urls[env];
  }

  getVsnFeedbackConfig(hostname: string): VsnFeedbackConfig {
    const env = this.getEnviroment(hostname);
    const configs: { [key: string]: VsnFeedbackConfig } = environment.vsnNavigation.feedback;
    return configs[env];
  }

  isUrlExcludedFromRetryStrategy(url: string): boolean {
    const arr: string[] = environment.excludedUrlsFromRetryStrategy;
    return this.indexOf(url, arr);
  }

  isConnectUrl(url: string): boolean {
    return (
      (this.isFromConnect(url, 'connect.') && !this.isFromConnect(url, '@')) ||
      this.isFromConnect(url, 'execute-api.eu-west-1.amazonaws.com')
    );
  }

  isConnectUserInfo(url: string): boolean {
    return (
      this.isFromConnect(url, '/connect/userinfo') ||
      this.isFromConnect(url, '/oauth2/userInfo')
    );
  }

  isContextsEndpoint(url: string): boolean {
    return (
      url.includes('/api/v1/contexts') ||
      url.includes('/v1/contexts')
    );
  }

  isCustomClaimsEndpoint(url: string): boolean {
    return url.includes('/v1/me/customClaims');
  }

  /**
   * The custom-claims write, and the one call that localhost sends to the real API instead of the
   * mock.
   *
   * Writing `lastUsedTenantId` is what makes a context switch stick: Connect binds each new token to
   * the tenant recorded there. Locally that PATCH went to the mock server, which answers 200 and
   * persists nothing - so selecting another context appeared to work and then landed back on whatever
   * stag last recorded, and no question about the tenant in a token could be answered locally at all.
   * The reads were always real (Connect userinfo), so only the write was pretending.
   *
   * Only localhost is redirected, and only to stag - which is where the rest of a local run already
   * points (Connect, Kalmar, the notifications hub). Every deployed environment keeps its own base,
   * so nothing outside a developer machine changes.
   *
   * Note this covers every write to that endpoint, theme and the welcome-dialog flag included. That is
   * the point rather than a side effect: those claims now behave locally the way they do deployed.
   */
  getBaseUrlForCustomClaims(hostname: string): string {
    const api: { [key: string]: string } = environment.api;
    return hostname === HOSTNAME.localhost ? api[ENV.stag] : this.getBaseUrl(hostname);
  }

  /**
   * Whether the request is for the Kalmar provisioning API rather than the access-management one.
   *
   * Decided by the `/api` the calling service writes into the path, not guessed from the entity.
   * The two APIs name the same entities - distributors, companies, users - so a path on its own
   * cannot say which is meant: `/v1/distributors/users` is access-management while
   * `/api/v1/distributors/{id}/products` is provisioning. Matching on segments like `products` or
   * `suites` held only while the overlap was small, and every entity added to provisioning made it
   * a worse guess.
   *
   * The base url therefore supplies the host only - the `/api` must NOT be appended there as well.
   *
   * Contexts are the deliberate exception and keep `/v1/contexts`: provisioning serves them on
   * localhost, int and stag while access-management serves them on prod, so their path cannot name
   * one API without breaking the other environment.
   */
  isProvisioningAPI(url: string): boolean {
    return url.split('?')[0].startsWith('/api/');
  }

  /**
   * Where the provisioning calls go, and the one place the rule below is written down.
   *
   * The tenants host, but bare: the path already carries the `/api` (see `isProvisioningAPI`), so
   * appending it here too would send `/api/api/v1/...`.
   *
   * A local run sends to the real service only the calls that decide **who and where you are** -
   * contexts, the custom-claims write, the onboarding tasks. Mock those and the whole local session
   * becomes fiction: the mocked custom-claims write is exactly why a context switch silently failed
   * to stick, and why every local answer about the tenant in a token was wrong for a while.
   *
   * Everything that only decides **what is on the screen** goes to the mock, like the rest of the
   * app. Products are that, even though their endpoint lives on the provisioning API - where an
   * endpoint is hosted is not the same question as what it decides.
   */
  getBaseUrlForProvisioningAPI(hostname: string): string {
    return hostname === HOSTNAME.localhost
      ? this.getBaseUrl(hostname)
      : this.getBaseUrlForTenants(hostname, true);
  }

  isOnboardingEndpoint(url: string): boolean {
    return url.includes('/v1/onboarding/tasks');
  }

  skipSpinner(url: string): boolean {
    return url.includes('skipSpinner=true');
  }

  isTestable(hostname: string, userEmail: string): boolean {
    return (
      this.isTestHostname(hostname) ||
      environment.testUsers.includes(userEmail)
    );
  }

  isTestHostname(hostname: string, excludeLocalhost?: boolean): boolean {
    // By environment, so every hostname of int or stag counts. Localhost stays a hostname check:
    // getEnviroment also resolves unknown hostnames to localhost, and those are not test hosts.
    const env = this.getEnviroment(hostname);
    const isDeployedTest = env === ENV.int || env === ENV.stag;
    return excludeLocalhost ? isDeployedTest : isDeployedTest || hostname === HOSTNAME.localhost;
  }

  getEnviroment(hostname: string): string {
    switch (hostname) {
      case HOSTNAME.int:
        return ENV.int;
      case HOSTNAME.stag:
      case HOSTNAME.stagScarlet:
        return ENV.stag;
      case HOSTNAME.prod:
      case HOSTNAME.prodScarlet:
        return ENV.prod;
      default:
        return ENV.localhost;
    }
  }

  private isFromConnect(url: string, segment: string): boolean {
    let is = false;
    if (url.includes(segment)) {
      is = true;
    }
    return is;
  }

  private indexOf(str: string, arr: string[]): boolean {
    return arr.some(item => str.includes(item));
  }
}
